<template>
    <div class="notes-bubble-card">
        <div class="notes-sidebar-header">
            <div class="sidebar-title">{{ $t("documents.title") }}</div>
            <div class="bubble-header-actions">
                <button class="bubble-icon-button" @click="emit('close')">
                    <img src="@/assets/images/svg/double_arrow_left.svg" :alt="$t('Common.close')">
                </button>
                <button class="bubble-icon-button">
                    <img src="@/assets/images/svg/import_outline.svg" :alt="$t('Common.download')">
                </button>
            </div>
        </div>
        <div class="note-categories">
            <button :class="{ active: selectedCategory === 'all' }" @click="selectCategory('all')">
                <span class="category-left"><span class="category-icon"><img :src="docsIcon" alt=""></span>{{ $t("documents.all_docs") }}</span>
                <span>{{ docCounts.all }}</span>
            </button>
            <button :class="{ active: selectedCategory === 'mine' }" @click="selectCategory('mine')">
                <span class="category-left"><span class="category-icon"><img :src="userIcon" alt=""></span>{{ $t("documents.my_docs_filter") }}</span>
                <span>{{ docCounts.mine }}</span>
            </button>
            <button :class="{ active: selectedCategory === 'shared' }" @click="selectCategory('shared')">
                <span class="category-left"><span class="category-icon"><img :src="shareIcon" alt=""></span>{{ $t("documents.shared_with_me") }}</span>
                <span>{{ docCounts.shared }}</span>
            </button>
        </div>
        <!-- <div class="sidebar-panel">
            <div class="panel-title">PINNED DOCS</div>
            <div v-if="pinnedDocs.length" class="pinned-list">
                <button v-for="doc in pinnedDocs" :key="doc.id" class="note-item" @click="openDocument(doc)">
                    <span>{{ doc.name || 'Untitled' }}</span>
                </button>
            </div>
            <div v-else class="panel-empty">
                <span class="pin-empty-icon"><img :src="pinIcon" alt=""></span>
                <p>Pin a Doc to see it here</p>
            </div>
        </div> -->
        <div class="sidebar-panel sidebar-panel--scroll">
            <div class="panel-title">{{ $t("documents.recent") }}</div>
            <ul class="recent-list">
                <li v-for="doc in recentDocs" :key="doc.id" @click="openDocument(doc)">
                    <span class="recent-file-icon"><img :src="recentIcon" alt=""></span>
                    <span class="recent-note-title">{{ doc.name || $t("Documents.untitled_document") }}</span>
                </li>
            </ul>
            <div v-if="!recentDocs.length" class="recent-empty">{{ $t("Documents.no_recent_docs") }}</div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { apiRequest } from '@/services'
import * as env from "@/config/env";

const { getters } = useStore();
const { t } = useI18n();
const docsIcon = require("@/assets/images/svg/file_smile.svg")
const userIcon = require("@/assets/images/svg/User_icon.svg")
const shareIcon = require("@/assets/images/svg/share_outline.svg")
// const pinIcon = require("@/assets/images/menu/pin.svg")
const recentIcon = require("@/assets/images/svg/file_gray.svg")

const emit = defineEmits(['close'])

const route = useRoute()
const currentUser = computed(() => getters["users/currentUser"])
const projectsGetter = computed(() => getters["projectData/onlyActiveProjects"])
const docs = ref([])
const selectedCategory = ref('all')
const currentUserId = computed(() => currentUser.value?._id || currentUser.value?.id || currentUser.value?.userId || "")
const projectIds = computed(() => {
    const data = Array.isArray(projectsGetter.value?.data)
        ? projectsGetter.value.data
        : Array.isArray(projectsGetter.value)
            ? projectsGetter.value
            : []

    return data.filter(project => project?._id && project._id !== "6571e7195470e64b1203295c" && !project.isRestrict).map(project => project._id)
})
const docCounts = computed(() => {
    const mine = docs.value.filter(doc => doc.creatorId && doc.creatorId === currentUserId.value).length

    return {
        all: docs.value.length,
        mine,
        shared: Math.max(docs.value.length - mine, 0),
    }
})
const filteredDocs = computed(() => {
    if (selectedCategory.value === 'mine') {
        return docs.value.filter(doc => doc.creatorId && doc.creatorId === currentUserId.value)
    }
    if (selectedCategory.value === 'shared') {
        return docs.value.filter(doc => !doc.creatorId || doc.creatorId !== currentUserId.value)
    }
    return docs.value
})
// const pinnedDocs = computed(() => filteredDocs.value.filter(doc => doc.isPin || doc.is_pin).slice(0, 5))
const recentDocs = computed(() => [...filteredDocs.value].sort((a, b) => (b.updatedAtMs || 0) - (a.updatedAtMs || 0)).slice(0, 5))

function selectCategory(category) {
    selectedCategory.value = category
    window.dispatchEvent(new CustomEvent("docs-category-filter", {
        detail: { category }
    }))
}

function openDocument(doc) {
    if (doc.url) window.open(doc.url, "_blank")
}

function getCreatorId(attachment, owner) {
    return attachment?.userId || attachment?.UserId || attachment?.createdBy || attachment?.uploadedBy || attachment?.uploaderId || owner?.createdBy || owner?.userId || owner?.UserId || owner?.projectCreatedBy || owner?.AssigneeUserId?.[0] || ""
}

function getDateMs(date) {
    if (!date) return 0
    const parsedDate = date?.seconds ? new Date(date.seconds * 1000) : new Date(date)
    return Number.isNaN(parsedDate.getTime()) ? 0 : parsedDate.getTime()
}

function createDocRow({ attachment, owner, source, resultIndex, attachmentIndex, taskIndex = 0 }) {
    const name = attachment?.filename || attachment?.mediaOriginalName || attachment?.name || t("Documents.untitled_document")

    return {
        id: `${source}-${resultIndex}-${taskIndex}-${attachmentIndex}-${name}`,
        name,
        url: attachment?.url || attachment?.mediaURL || "",
        creatorId: getCreatorId(attachment, owner),
        updatedAtMs: getDateMs(attachment?.updatedAt || attachment?.modifiedAt || attachment?.createdAt),
        isPin: attachment?.isPin || attachment?.is_pin,
    }
}

function mapFilesToDocs(results = []) {
    const rows = []

    results.forEach((result, resultIndex) => {
        const projectAttachments = result?.attachments || []
        const taskAttachments = result?.taskAttachment || []
        const commentAttachments = result?.commentsAttachment || []

        projectAttachments.forEach((attachment, attachmentIndex) => {
            rows.push(createDocRow({ attachment, owner: result, source: "project", resultIndex, attachmentIndex }))
        })
        taskAttachments.forEach((taskAttachment, taskIndex) => {
            const attachments = taskAttachment?.attachments || []

            attachments.forEach((attachment, attachmentIndex) => {
                rows.push(createDocRow({ attachment, owner: taskAttachment, source: "task", resultIndex, attachmentIndex, taskIndex }))
            })
        })
        commentAttachments.forEach((attachment, attachmentIndex) => {
            rows.push(createDocRow({ attachment, owner: attachment, source: "comment", resultIndex, attachmentIndex }))
        })
    })

    return rows
}

async function fetchDocs() {
    if (!projectIds.value.length) return

    try {
        const batchSize = 500
        let skip = 0
        let allResults = []
        let hasMore = true

        while (hasMore) {
            const res = await apiRequest("post", `${env.ADVANCE_GLOBAL_FILTER}/search/files`, {
                filterQuery: {},
                pids: projectIds.value,
                skipValue: skip,
                batchSizeValue: batchSize,
                sortBy: "last_update",
                searchText: "",
            })

            const batch = res?.data?.data || []
            allResults = [...allResults, ...batch]
            hasMore = batch.length === batchSize
            skip += batchSize
        }

        docs.value = mapFilesToDocs(allResults)
    } catch (err) {
        console.error('Fetch docs error:', err)
    }
}

watch(() => route.name, (name) => {
    if (name === 'documents') {
        fetchDocs()
    }
})

watch(projectIds, () => {
    if (route.name === 'documents') {
        fetchDocs()
    }
})

onMounted(() => {
    if (route.name === 'documents') {
        fetchDocs()
        selectCategory(selectedCategory.value)
    }
})
</script>

<style scoped src="./style.css">
</style>
